<template>
  <div class="page">
    <header class="toolbar">
      <div class="controls controls--split">
        <div class="controls-left">
          <div class="refresh-dropdown">
            <button
              class="refresh-btn"
              :disabled="refreshing"
              @click="toggleRefreshMenu"
            >
              <span v-if="!refreshing">Updaten ▼</span>
              <span v-else class="spinner small"></span>
            </button>
            <div v-if="showRefreshMenu && !refreshing" class="refresh-menu">
              <button
                class="refresh-menu-item"
                @click="refreshDataByType(null)"
              >
                Alles
              </button>
              <button
                v-for="prog in programsWithTypes"
                :key="prog.type"
                class="refresh-menu-item"
                @click="refreshDataByType(prog.programType)"
              >
                {{ prog.type }}
              </button>
            </div>
          </div>
        </div>
        <div class="controls-right">
          <label class="field">
            <select v-model="selectedProgram">
              <option v-for="p in programs" :key="p" :value="p">{{ p }}</option>
            </select>
          </label>
          <div class="week-buttons">
            <button
              v-for="(d, idx) in weekButtons"
              :key="idx"
              class="day-btn"
              :class="{
                active: d.dateStr === selectedDate,
                'no-workout': !d.hasWorkout,
              }"
              @click="selectDay(d.dateStr)"
            >
              {{ d.label }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Error alert -->
    <div
      v-if="error"
      class="alert alert--error"
      role="alert"
      aria-live="assertive"
    >
      <span class="alert-text">{{ error }}</span>
      <button
        class="alert-close"
        @click="error = ''"
        aria-label="Melding sluiten"
      >
        ×
      </button>
    </div>

    <main
      class="stage"
      @gesturestart="onGestureStart"
      @gesturechange="onGestureChange"
      @gestureend="onGestureEnd"
    >
      <button class="nav left" @click="prevSlide" aria-label="Vorige">
        &#x2190;
      </button>

      <div ref="slide" class="slide" :style="slideStyle">
        <template v-if="currentSlide && currentSlide.kind === 'summary'">
          <h2 class="headline">{{ currentTitle }}</h2>
          <ul class="items">
            <li v-for="(name, i) in summaryNames" :key="i">{{ name }}</li>
          </ul>
        </template>
        <template v-else-if="currentSlide && currentSlide.kind === 'detail'">
          <h2 class="headline">{{ currentDetailTitle }}</h2>
          <div class="item-note" v-html="currentDetailHtml"></div>
        </template>
        <template v-else>
          <h2 class="headline">{{ currentTitle }}</h2>
          <div class="no-workout">Geen workout voor deze datum.</div>
        </template>
      </div>

      <button class="nav right" @click="nextSlide" aria-label="Volgende">
        &#x2192;
      </button>
    </main>

    <footer class="pager">{{ slideIndex + 1 }} / {{ slides.length }}</footer>

    <!-- Fit-zoom FAB – stays visible outside the scaled content -->
    <button
      class="fit-zoom-fab"
      :class="{ active: isFitZoom }"
      @click="toggleFitZoom"
      aria-label="Zoom passend"
    >
      ⌕
    </button>

    <div v-if="loading || refreshing" class="loading-overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
// Production: relative URLs (nginx reverse proxies /api/* to backend).
// Development (localhost): absolute URL to deployed backend.
const isDev =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";
const BASE_URL = isDev ? "https://cfnd-wods-api.codeer-meneer.nl" : "";
const API_URL = `${BASE_URL}/api/workouts`;
const SCRAPER_URL = `${BASE_URL}/api/workouts/scrape`;

export default {
  name: "ProgramPage",
  data() {
    const today = new Date();
    const start = startOfISOWeek(today);
    const end = addDays(start, 13); // huidige week (ma-zo) + volgende week (ma-zo)
    return {
      // Data
      rawData: [],
      selectedProgram: "",

      // Date handling (current week only)
      todayStr: toInputDate(today),
      minDate: toInputDate(start),
      maxDate: toInputDate(end),
      selectedDate: toInputDate(today),

      // Slides per geselecteerde dag (summary + details)
      slideIndex: 0,
      days: [], // beschikbare dagen binnen bereik: [{ dateStr, notities }]
      slides: [], // huidige dag slides: [{ kind: 'summary'|'detail', ... }]

      // UI
      loading: false,
      refreshing: false,
      isFullscreen: false,
      error: "",
      showRefreshMenu: false,

      // Zoom (iOS Safari fullscreen)
      zoom: 1,
      isFitZoom: false,
      fitFontSize: null, // px value when fit-zoom is active
    };
  },
  computed: {
    programs() {
      const set = new Set((this.rawData || []).map((c) => c.type));
      return Array.from(set);
    },
    programsWithTypes() {
      // Return array of { type: string, programType: int }
      return (this.rawData || []).map((c) => ({
        type: c.type,
        programType: c.programType,
      }));
    },
    currentSlide() {
      return this.slides[this.slideIndex] || null;
    },
    currentTitle() {
      const d = fromInputDate(this.selectedDate);
      const formatter = new Intl.DateTimeFormat("nl-NL", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return capitalize(formatter.format(d));
    },
    summaryNames() {
      const s = this.currentSlide;
      return s && s.kind === "summary" ? s.names : [];
    },
    currentDetailTitle() {
      const s = this.currentSlide;
      return s && s.kind === "detail" ? s.title : "";
    },
    currentDetailHtml() {
      const s = this.currentSlide;
      if (!s || s.kind !== "detail") return "";
      const raw = s.html || "";
      // Already HTML — return as-is
      if (raw.includes("<p") || raw.includes("<br")) return raw;
      // Plain text — convert newlines to <br>
      return "<p>" + raw.replace(/\n/g, "<br>") + "</p>";
    },
    weekButtons() {
      const start = startOfISOWeek(fromInputDate(this.selectedDate));
      const labels = ["ma", "di", "wo", "do", "vr", "za", "zo"];
      const arr = [];
      for (let i = 0; i < 7; i++) {
        const d = addDays(start, i);
        const ds = toInputDate(d);
        const hasWorkout = (this.days || []).some((day) => day.dateStr === ds);
        arr.push({ label: labels[i], dateStr: ds, hasWorkout });
      }
      return arr;
    },
    slideStyle() {
      if (this.isFitZoom && this.fitFontSize) {
        return { fontSize: `${this.fitFontSize}px` };
      }
      const s = Math.max(1, Math.min(this.zoom || 1, 4));
      return {
        transform: `scale(${s})`,
        transformOrigin: "center center",
      };
    },
  },

  watch: {
    selectedProgram() {
      this.recomputeSlides();
    },
    selectedDate() {
      this.buildSlidesForSelectedDate();
      this.slideIndex = 0;
    },
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.onFsChange);
    document.addEventListener("click", this.handleClickOutside);
    this.fetchData();
  },
  beforeUnmount() {
    document.removeEventListener("fullscreenchange", this.onFsChange);
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    onFsChange() {
      this.isFullscreen = !!document.fullscreenElement;
      if (!this.isFullscreen) this.zoom = 1; // reset zoom when leaving fullscreen
    },
    toggleRefreshMenu(event) {
      event.stopPropagation();
      this.showRefreshMenu = !this.showRefreshMenu;
    },
    handleClickOutside(event) {
      const dropdown = event.target.closest(".refresh-dropdown");
      if (!dropdown && this.showRefreshMenu) {
        this.showRefreshMenu = false;
      }
    },
    async fetchData() {
      try {
        this.loading = true;
        const res = await fetch(API_URL);
        console.log(res);
        const data = await res.json();
        const workouts = data.workouts;
        this.rawData = Array.isArray(workouts) ? workouts : [];

        // Init default program
        if (!this.selectedProgram && this.programs.length) {
          this.selectedProgram = this.programs[0];
        }
        this.recomputeSlides();
      } catch (e) {
        this.error = "Kon workouts niet ophalen.";
        // console.error(e);
      } finally {
        this.loading = false;
      }
    },
    recomputeSlides() {
      const cat = (this.rawData || []).find(
        (c) => c.type === this.selectedProgram,
      );
      if (!cat) {
        this.slides = [];
        this.slideIndex = 0;
        return;
      }

      const weekStart = fromInputDate(this.minDate);
      const weekEnd = fromInputDate(this.maxDate);

      const days = (cat.items || [])
        .map((it) => ({
          date: parseApiDate(it.datum),
          notities: it.notities || [],
        }))
        .filter((x) => x.date >= weekStart && x.date <= weekEnd)
        .sort((a, b) => a.date - b.date)
        .map((x) => ({ dateStr: toInputDate(x.date), notities: x.notities }));

      this.days = days;
      // Laat de geselecteerde datum staan; toon melding als er geen workout is.
      this.buildSlidesForSelectedDate();
    },
    buildSlidesForSelectedDate() {
      const selTs = fromInputDate(this.selectedDate).getTime();
      const entry = (this.days || []).find(
        (d) => fromInputDate(d.dateStr).getTime() === selTs,
      );
      if (!entry) {
        this.slides = [];
        this.slideIndex = 0;
        return;
      }

      const names = (entry.notities || []).map((n) => n.naam);
      const slides = [];
      // Slide 1: samenvatting (alle namen)
      slides.push({ kind: "summary", dateStr: this.selectedDate, names });
      // Volgende slides: detail per notitie (met html)
      (entry.notities || []).forEach((n) => {
        slides.push({ kind: "detail", title: n.naam, html: n.notitie });
      });

      this.slides = slides;
      if (this.slideIndex >= this.slides.length) this.slideIndex = 0;
    },

    syncSlideToSelectedDate() {
      const idx = this.slides.findIndex((s) => s.dateStr === this.selectedDate);
      if (idx >= 0) {
        this.slideIndex = idx;
        return true;
      }
      return false;
    },
    onDatePicked() {
      this.buildSlidesForSelectedDate();
      this.slideIndex = 0;
    },
    selectDay(dateStr) {
      this.selectedDate = dateStr;
    },
    async refreshData() {
      await this.refreshDataByType(null);
    },
    async refreshDataByType(programType) {
      try {
        this.refreshing = true;
        this.showRefreshMenu = false;

        // Scraper aanroepen; fetch reject niet bij HTTP fouten, dus zelf checken
        const controller = new AbortController();
        const res = await fetch(SCRAPER_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ programType }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(
            `Scraper HTTP ${res.status} ${res.statusText} ${text}`,
          );
        }

        // delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Na scrape opnieuw de data ophalen
        await this.fetchData();
      } catch (e) {
        console.log(e);
        this.error = "Updaten van workouts mislukt.";
      } finally {
        this.refreshing = false;
      }
    },

    // iOS Safari pinch zoom via non-standard gesture events
    onGestureStart(e) {
      // Alleen gebruiken in fullscreen zodat systeem-zoom niet tussenbeide komt
      if (!document.fullscreenElement) return;
      e.preventDefault();
      this._gestureStartZoom = this.zoom || 1;
    },
    onGestureChange(e) {
      if (!document.fullscreenElement) return;
      e.preventDefault();
      const base = this._gestureStartZoom || 1;
      // e.scale: 1 = geen zoom, >1 = inzoomen
      const next = base * (e.scale || 1);
      this.zoom = Math.max(1, Math.min(next, 4));
    },
    onGestureEnd(e) {
      if (!document.fullscreenElement) return;
      e.preventDefault();
      // optioneel: afronden of grenzen afdwingen (al gebeurd in change)
    },

    toggleFitZoom() {
      if (this.isFitZoom) {
        this.isFitZoom = false;
        this.fitFontSize = null;
        this.zoom = 1;
        return;
      }

      // Reset any existing zoom first
      this.fitFontSize = null;
      this.zoom = 1;

      this.$nextTick(() => {
        this.applyFitZoom();
        this.isFitZoom = true;
      });
    },
    nextSlide() {
      if (!this.slides.length) return;
      if (this.slideIndex < this.slides.length - 1) {
        this.slideIndex++;
        this.reapplyFitZoom();
      }
    },
    prevSlide() {
      if (!this.slides.length) return;
      if (this.slideIndex > 0) {
        this.slideIndex--;
        this.reapplyFitZoom();
      }
    },
    reapplyFitZoom() {
      if (!this.isFitZoom) return;
      // Temporarily clear so the new slide renders at natural size for measuring
      this.fitFontSize = null;
      this.$nextTick(() => {
        this.applyFitZoom();
      });
    },
    applyFitZoom() {
      const slide = this.$refs.slide;
      const stage = slide?.parentElement;
      if (!slide || !stage) return;

      const stageRect = stage.getBoundingClientRect();
      const availH = stageRect.height - 40;
      const availW = stageRect.width - 160;

      let lo = 8;
      let hi = 120;

      while (hi - lo > 1) {
        const mid = Math.floor((lo + hi) / 2);
        slide.style.fontSize = `${mid}px`;
        const h = slide.scrollHeight;
        const w = slide.scrollWidth;
        if (h <= availH && w <= availW) {
          lo = mid;
        } else {
          hi = mid;
        }
      }

      slide.style.fontSize = "";
      this.fitFontSize = lo;
    },
  },
};

// -------- helpers --------
function startOfISOWeek(d) {
  const date = new Date(d);
  const day = date.getDay(); // 0 (Sun) - 6 (Sat)
  const diff = (day === 0 ? -6 : 1) - day; // shift to Monday
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}
function addDays(d, n) {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}
function toInputDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function fromInputDate(s) {
  // Parse "YYYY-MM-DD" into a LOCAL date (avoid UTC parsing drift)
  const [yyyy, mm, dd] = String(s || "").split("-");
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  d.setHours(0, 0, 0, 0);
  return d;
}
function parseApiDate(s) {
  // input like " 02-09-2025" (with possible leading space)
  const t = String(s || "").trim();
  const [dd, mm, yyyy] = t.split("-");
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  d.setHours(0, 0, 0, 0);
  return d;
}
function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: #111;
  color: #ddd;
  display: flex;
  flex-direction: column;
}
.toolbar {
  background: #1b1b1b;
  border-bottom: 1px solid #2a2a2a;
  padding: 10px 16px;
}

/* Error alert */
.alert {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  margin: 10px 16px 0 16px;
  border: 1px solid transparent;
}
.alert--error {
  background: #3c1111;
  color: #f2caca;
  border-color: #6a1a1a;
}
.alert-close {
  background: transparent;
  color: inherit;
  border: none;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
}

.controls {
  display: flex;
  gap: 16px;
  align-items: center;
}
.field {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #aaa;
}
.field select,
.field input[type="date"] {
  margin-top: 4px;
  background: #222;
  color: #eee;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 8px 10px;
}
.stage {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.slide {
  text-align: center;
}
.headline {
  color: #ffd84d;
  margin-bottom: 16px;
  font-size: 1.4em;
}
.items {
  list-style: none;
  padding: 0;
  margin: 0;
}
.items li {
  margin: 6px 0;
}

.nav {
  position: absolute;
  bottom: 20px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #2a2a2a;
  color: #fff;
  border: 1px solid #3a3a3a;
  font-size: 28px;
  line-height: 64px;
  text-align: center;
  cursor: pointer;
}
.nav.left {
  left: 20px;
}
.nav.right {
  right: 20px;
}
.nav:hover {
  background: #3a3a3a;
}

.pager {
  text-align: right;
  color: #888;
  font-size: 12px;
  padding: 0 20px 16px;
}
.no-workout {
  color: #bbb;
  margin-top: 12px;
  font-size: 16px;
}
.controls--split {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.controls-left {
  display: flex;
  gap: 12px;
  align-items: center;
}
.controls-right {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-left: auto;
}
.controls {
  gap: 20px;
  font-size: 16px;
}
.field select,
.field input[type="date"] {
  padding: 10px 12px;
  font-size: 16px;
}
.icon-btn {
  background: #222;
  color: #eee;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
}
.icon-btn.fullscreen {
  font-size: 18px;
}
.week-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}
.day-btn {
  background: #222;
  color: #eee;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 10px 12px;
  min-width: 44px;
  cursor: pointer;
}
.day-btn.active {
  background: #ffd84d;
  color: #111;
  border-color: #e6c33f;
}
.day-btn.no-workout {
  background: #444;
  color: #888;
  border-color: #555;
}
.day-btn.no-workout.active {
  background: #666;
  color: #ccc;
  border-color: #777;
}
.refresh-dropdown {
  position: relative;
}
.refresh-btn {
  background: #2d6cdf;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
}
.refresh-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}
.refresh-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: #1b1b1b;
  border: 1px solid #333;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  min-width: 180px;
  overflow: hidden;
}
.refresh-menu-item {
  display: block;
  width: 100%;
  background: transparent;
  color: #eee;
  border: none;
  padding: 10px 14px;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}
.refresh-menu-item:hover {
  background: #2a2a2a;
}
.refresh-menu-item:first-child {
  font-weight: 600;
  border-bottom: 1px solid #333;
}
/* Fit-zoom floating action button */
.fit-zoom-fab {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #2a2a2a;
  color: #fff;
  border: 1px solid #3a3a3a;
  font-size: 22px;
  line-height: 48px;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}
.fit-zoom-fab:hover {
  background: #3a3a3a;
}
.fit-zoom-fab.active {
  background: #ffd84d;
  color: #111;
  border-color: #e6c33f;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.spinner.small {
  width: 18px;
  height: 18px;
  border-width: 3px;
  display: inline-block;
  vertical-align: middle;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile layout fixes */
@media (max-width: 768px) {
  .controls--split {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .controls-left,
  .controls-right {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .field {
    width: 100%;
  }
  .field select,
  .field input[type="date"] {
    width: 100%;
  }
  .week-buttons {
    flex-wrap: wrap;
    gap: 6px;
  }
  .day-btn {
    flex: 1 1 calc(14% - 6px);
    min-width: 44px;
    padding: 8px 10px;
  }
  .stage {
    padding: 16px;
  }
  .headline {
    font-size: 20px;
  }
  .refresh-btn {
    padding: 10px 12px;
  }
}
</style>
